import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { VirtualKeyboardTarget } from './virtual-keyboard.types'

/**
 * Coordena o teclado virtual do totem: guarda qual campo está ativo e propaga o
 * texto digitado de volta pro input, disparando o evento `input` nativo para que
 * ngModel/formControlName percebam a mudança (o Angular escuta `input`, e não a
 * escrita direta em `element.value`).
 *
 * Existe UMA instância do teclado por fluxo (renderizada no container do
 * autoatendimento) e vários campos que se registram nela via
 * `VirtualKeyboardDirective`.
 */
@Injectable({ providedIn: 'root' })
export class VirtualKeyboardService {
  private readonly targetSubject = new BehaviorSubject<VirtualKeyboardTarget | null>(null)
  /** Altura ocupada pelo teclado, publicada para as telas afastarem o conteúdo. */
  private readonly heightSubject = new BehaviorSubject<number>(0)

  /** Campo ativo (null = teclado fechado). */
  get target$(): Observable<VirtualKeyboardTarget | null> {
    return this.targetSubject.asObservable()
  }

  get target(): VirtualKeyboardTarget | null {
    return this.targetSubject.value
  }

  /** Altura atual do teclado em pixels (0 quando fechado). */
  get height$(): Observable<number> {
    return this.heightSubject.asObservable()
  }

  /** Abre o teclado ligado ao campo informado. */
  open(target: VirtualKeyboardTarget): void {
    this.targetSubject.next(target)
  }

  /** Fecha o teclado (nenhum campo ativo). */
  close(): void {
    if (this.targetSubject.value) {
      this.targetSubject.next(null)
    }
    this.setHeight(0)
  }

  /** Fecha somente se o campo informado ainda for o ativo (evita fechar durante troca de foco). */
  closeIfTarget(input: HTMLInputElement): void {
    if (this.targetSubject.value?.input === input) {
      this.close()
    }
  }

  /** Publica a altura ocupada pelo teclado para as telas reposicionarem o conteúdo. */
  setHeight(height: number): void {
    if (this.heightSubject.value !== height) {
      this.heightSubject.next(height)
    }
  }

  /**
   * Escreve o valor no input ativo e notifica o Angular.
   * Retorna o valor efetivamente aplicado (já respeitando o maxlength do campo).
   */
  writeToTarget(value: string): string {
    const target = this.targetSubject.value
    if (!target) {
      return ''
    }

    const limited =
      target.maxLength && target.maxLength > 0 ? value.substring(0, target.maxLength) : value

    target.input.value = limited
    target.input.dispatchEvent(new Event('input', { bubbles: true }))

    return limited
  }
}
